import { useRequest } from 'ahooks';
import { Col, DatePicker, Form, Input, Row, Select, Space, Table, Tag, Descriptions } from 'antd';
import LocaleContext from 'locales';
import moment from 'moment';
import React, { useContext, useState, useEffect } from 'react';
import { getAssignedDriveDetail, getAssignedDrives } from 'services/websocket/approval';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { DriveDescription, RequestInfo } from './components';
import FormItemApprovalLine from './components/FormItemApprovalLine';

const StyledTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  > .ant-tag {
    margin-right: 0px;
  }
`;
const styles = {
  spaceBetween: { display: 'flex', justifyContent: 'space-between' },
};

export default function DriveUpdatePermission(props) {
  // variables
  const { detailData, form, id, isCreate, setIsWaitState, type, onFinishDetail, backConf } = props;
  const [beforeContents, setBeforeContents] = useState({});
  const [errorVisible, setErrorVisible] = useState(false);

  const [contentsData, setContentsData] = useState([]);

  // hooks
  const fetchDrives = useRequest(getAssignedDrives, { defaultParams: [{ type }] });
  const fetchDriveDetail = useRequest(getAssignedDriveDetail, { manual: true });
  const { locale } = useContext(LocaleContext);

  useEffect(() => {
    if (!id) {
      const applicant = localStorage.getItem('userId');
      form.setFieldsValue({
        applicant,
      });

      return;
    }

    if (detailData) {
      const contents = detailData?.contents;

      let approveDate = detailData?.approveDate;
      if (approveDate !== null && approveDate !== undefined) {
        approveDate = moment(approveDate);
      }

      // 사용자 결재인 경우
      if (detailData.approvalLines) {
        const newApprovalLines = detailData.approvalLines.map((el) => ({
          ...el,
          key: uuidv4(),
        }));
        detailData.approvalLines = newApprovalLines;
      }

      form.setFieldsValue({
        ...detailData,
        approveDate,
        contents: {
          ...contents[0],
          driveId: contents[0].driveName,
        },
        applyDate: moment(detailData.applyDate).format('YYYY-MM-DD HH:mm:ss'),
      });

      if (setIsWaitState) {
        setIsWaitState(detailData.state === 'approval.state.waiting');
      }
    }
  }, [id, detailData]);

  // 저장 시
  const onFinish = async (values) => {
    setErrorVisible(false);
    const errObject = [];

    if (contentsData.owners?.length === 0) {
      errObject.push(
        `${locale.resource['txt.required-field']}(${locale.resource['network-drive.form.owner']})`,
      );
    }
    if (contentsData.admins?.length === 0) {
      errObject.push(`${locale.resource['txt.required-field']}(${locale.resource['txt.admin']})`);
    }
    if (contentsData.permissions?.length === 0) {
      errObject.push(
        `${locale.resource['txt.required-field']}(${locale.resource['common.permission']})`,
      );
    }

    if (errObject.length > 0) {
      setErrorVisible(true);
      form.setFields([{ name: ['contents'], errors: errObject }]);
      return;
    }

    const payload = {
      ...values,
      type,
      user: values.applicant,
      contents: [
        {
          ...beforeContents,
          owners: contentsData.owners,
          admins: contentsData.admins,
          permissions: contentsData.permissions,
          beforeContents: {
            owners: beforeContents.owners,
            admins: beforeContents.admins,
            permissions: beforeContents.permissions,
          },
        },
      ],
    };
    onFinishDetail(payload);
  };

  return (
    <Form
      layout="horizontal"
      labelCol={{ span: 4 }}
      wrapperCol={{ span: 20 }}
      form={form}
      onFinish={onFinish}
    >
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <RequestInfo readOnly={!isCreate} form={form} detailData={detailData} />
          <Form.Item label={locale.resource['txt.approval-type']}>
            <Input readOnly bordered={false} value={locale.resource[detailData?.type || type]} />
          </Form.Item>
          <Form.Item name="user" initialValue={localStorage.getItem('userId')} hidden>
            <Input />
          </Form.Item>
          <Form.Item
            label={locale.resource['approval.drive.select']}
            rules={[{ required: true, message: locale.resource['txt.required-field'] }]}
            name={['contents', 'driveId']}
          >
            <Select
              loading={fetchDrives.loading}
              allowClear
              options={
                fetchDrives.data
                  ?.filter((el) => el.driveType !== 'individual')
                  .map((el) => ({
                    label: el.driveName,
                    value: el.driveId,
                    rowData: el,
                  })) || []
              }
              disabled={!isCreate}
              defaultValue={
                detailData &&
                (fetchDrives.data?.some((driver) => driver.driveId !== detailData.contents[0].id)
                  ? detailData.contents[0].driveName
                  : detailData.contents[0].id)
              }
              onSelect={async (value, option) => {
                /*
                  TODO http://redmine.somansa.com/redmine/issues/198217 
                  이유는 모르겟으나 데이터가 변경되면 같이 변경되는 이슈가 있어 아래처럼 사용
                  추후 수정 필수
                */
                await fetchDriveDetail.runAsync(option.rowData);
                const data = await getAssignedDriveDetail(option.rowData);
                setBeforeContents(data);
              }}
              onChange={(value) => {
                // 선택을 취소한 경우
                if (!value) {
                  fetchDriveDetail.mutate(undefined);
                }
              }}
            />
          </Form.Item>
          <Row style={{ marginBottom: 20 }}>
            <Col span={20} offset={4}>
              {!isCreate && (
                <Space style={styles.spaceBetween}>
                  <div>{locale.resource['txt.before']}</div>
                </Space>
              )}
              <DriveDescription
                data={!isCreate ? detailData?.contents[0] : fetchDriveDetail.data}
                type={type}
                setContentsData={setContentsData}
                isCreate={isCreate}
              />
              {errorVisible && <Form.Item name="contents" />}
            </Col>
          </Row>
          {!isCreate && detailData && (
            <>
              <Row style={{ marginBottom: 20 }}>
                <Col span={20} offset={4}>
                  <Space style={styles.spaceBetween}>
                    <div>{locale.resource['txt.after']}</div>
                  </Space>
                  <Descriptions column={1} bordered size="middle" labelStyle={{ width: 120 }}>
                    <Descriptions.Item
                      label={
                        <Space style={styles.spaceBetween}>
                          <div>{locale.resource['network-drive.form.owner']}</div>
                        </Space>
                      }
                    >
                      <StyledTags>
                        {detailData?.contents[0].owners?.map((el) => (
                          <Tag
                            color="cyan"
                            style={{
                              maxWidth: 120,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            closable={
                              isCreate &&
                              type !== 'approval.types.delete-drive' &&
                              type !== 'approval.types.modify-drive-size'
                            }
                          >
                            {el.userName || el.name}
                          </Tag>
                        ))}
                      </StyledTags>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <Space style={styles.spaceBetween}>
                          <div>{locale.resource['txt.admin']}</div>
                        </Space>
                      }
                    >
                      <StyledTags>
                        {detailData?.contents[0].admins?.map((el) => (
                          <Tag
                            color="orange"
                            style={{
                              maxWidth: 120,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            closable={
                              isCreate &&
                              type !== 'approval.types.delete-drive' &&
                              type !== 'approval.types.modify-drive-size'
                            }
                          >
                            {el.userName || el.name}
                          </Tag>
                        ))}
                      </StyledTags>
                    </Descriptions.Item>
                    <Descriptions.Item
                      label={
                        <Space style={styles.spaceBetween}>
                          <div>{locale.resource['common.permission']}</div>
                        </Space>
                      }
                    >
                      <Table
                        size="small"
                        pagination={false}
                        columns={[
                          {
                            title: locale.resource['network-drive.column.username.with.id'],
                            dataIndex: 'userName',
                            render: (value, record) =>
                              `${record.userName || record.name}(${record.sAMAccountName})`,
                          },
                          {
                            title: locale.resource['network-drive.column.department'],
                            render: (value, record) => record.organization,
                          },
                          {
                            title: locale.resource['common.permission'],
                            dataIndex: 'permission',
                            width: 100,
                            render: (value) => (
                              <Select
                                defaultValue={value || 'writable'}
                                style={{ width: '100%' }}
                                disabled={
                                  !isCreate ||
                                  type === 'approval.types.delete-drive' ||
                                  type === 'approval.types.modify-drive-size'
                                }
                                options={[
                                  {
                                    label: locale.resource['permission.control.option.readWrite'],
                                    value: 'writable',
                                  },
                                  {
                                    label: locale.resource['permission.control.option.readOnly'],
                                    value: 'readonly',
                                  },
                                  {
                                    label: locale.resource['permission.control.option.invalid'],
                                    value: 'invalid',
                                  },
                                ]}
                              />
                            ),
                          },
                          {
                            title: locale.resource['network-drive.column.expire.date'],
                            dataIndex: 'expireDate',
                            width: 120,
                            render: (value) => (
                              <DatePicker
                                defaultValue={value && moment(value, 'YYYY-MM-DD')}
                                disabled={
                                  !isCreate ||
                                  type === 'approval.types.delete-drive' ||
                                  type === 'approval.types.modify-drive-size'
                                }
                              />
                            ),
                          },
                        ]}
                        dataSource={detailData?.contents[0]?.permissions || []}
                      />
                    </Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            </>
          )}
          <FormItemApprovalLine isCreate={isCreate} form={form} backConf={backConf} type={type} />
        </Col>
      </Row>
    </Form>
  );
}
